
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { cartUtils, optimizedFetch, performanceMonitor } from '../../utils/performance';

const CartPage = () => {
  const [cart, setCart] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'France',
  });
  const [billingInfo, setBillingInfo] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'France',
    sameAsShipping: true,
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Load cart from localStorage on component mount
  useEffect(() => {
    performanceMonitor.mark('cart-load-start');
    const savedCart = cartUtils.getCart();
    setCart(savedCart);
    performanceMonitor.mark('cart-load-end');
    performanceMonitor.measure('cart-load', 'cart-load-start', 'cart-load-end');
  }, []);

  // Listen for cart updates
  useEffect(() => {
    const handleCartUpdate = (event: CustomEvent) => {
      setCart(event.detail || cartUtils.getCart());
    };

    window.addEventListener('cartUpdated', handleCartUpdate as EventListener);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate as EventListener);
  }, []);

  const updateQuantity = (cartId: string, newQuantity: number) => {
    const updatedCart = cartUtils.updateQuantity(cartId, newQuantity);
    setCart(updatedCart);
  };

  const removeFromCart = (cartId: string) => {
    const updatedCart = cartUtils.removeFromCart(cartId);
    setCart(updatedCart);
  };

  const subtotal = cartUtils.getCartTotal();
  const shippingCost = subtotal >= 90 ? 0 : 5.90;
  const total = subtotal + shippingCost;

  // Enhanced validation
  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    // Required fields validation
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'postalCode'];
    requiredFields.forEach(field => {
      if (!shippingInfo[field as keyof typeof shippingInfo]) {
        newErrors[field] = 'Ce champ est obligatoire';
      }
    });

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (shippingInfo.email && !emailRegex.test(shippingInfo.email)) {
      newErrors.email = 'Adresse email invalide';
    }

    // Phone validation (French format)
    const phoneRegex = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
    if (shippingInfo.phone && !phoneRegex.test(shippingInfo.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Numéro de téléphone français invalide (ex: 06 12 34 56 78)';
    }

    // Postal code validation (French format)
    const postalCodeRegex = /^[0-9]{5}$/;
    if (shippingInfo.postalCode && !postalCodeRegex.test(shippingInfo.postalCode)) {
      newErrors.postalCode = 'Code postal français invalide (5 chiffres)';
    }

    // Billing validation if different from shipping
    if (!billingInfo.sameAsShipping) {
      const billingRequiredFields = ['firstName', 'lastName', 'address', 'city', 'postalCode'];
      billingRequiredFields.forEach(field => {
        if (!billingInfo[field as keyof typeof billingInfo]) {
          newErrors[`billing_${field}`] = 'Ce champ de facturation est obligatoire';
        }
      });

      if (billingInfo.postalCode && !postalCodeRegex.test(billingInfo.postalCode)) {
        newErrors.billing_postalCode = 'Code postal de facturation invalide (5 chiffres)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      alert('Votre panier est vide');
      return;
    }

    if (!validateForm()) {
      const firstError = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstError}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsLoading(true);
    performanceMonitor.mark('checkout-start');

    try {
      const response = await optimizedFetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-cart-payment-optimized`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            items: cart.map(item => ({
              name: item.name,
              price: item.price,
              quantity: item.quantity,
              selectedSize: item.selectedSize,
              image: item.image
            })),
            shippingInfo: {
              ...shippingInfo,
              phone: shippingInfo.phone.replace(/\s/g, '') // Clean phone number
            },
            billingInfo: billingInfo.sameAsShipping ? shippingInfo : {
              ...billingInfo,
              email: shippingInfo.email // Use shipping email for billing
            },
            shippingCost,
            total,
          }),
        },
        20000, // 20 second timeout
        3 // 3 retries
      );

      const data = await response.json();
      
      if (data.url) {
        // Save order info before redirect
        const orderInfo = {
          items: cart,
          shippingInfo,
          billingInfo,
          total,
          timestamp: Date.now()
        };
        
        if (!cartUtils.setCart([])) { // Clear cart
          console.warn('Failed to clear cart');
        }
        
        localStorage.setItem('vybe_pending_order', JSON.stringify(orderInfo));
        
        performanceMonitor.mark('checkout-end');
        performanceMonitor.measure('checkout-process', 'checkout-start', 'checkout-end');
        
        window.location.href = data.url;
      } else {
        throw new Error('URL de paiement non reçue du serveur');
      }
    } catch (error: any) {
      console.error('Erreur checkout:', error);
      
      let errorMessage = 'Erreur lors de la création du paiement. Veuillez réessayer.';
      
      if (error.name === 'AbortError') {
        errorMessage = 'La connexion prend trop de temps. Veuillez vérifier votre connexion internet et réessayer.';
      } else if (error.message.includes('HTTP 4')) {
        errorMessage = 'Erreur de validation des données. Veuillez vérifier vos informations.';
      } else if (error.message.includes('HTTP 5')) {
        errorMessage = 'Erreur serveur temporaire. Veuillez réessayer dans quelques instants.';
      }
      
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]" style={{ fontFamily: 'Inter, sans-serif' }}>
        <Header />
        <main className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center py-20">
            <div className="bg-gray-900 rounded-lg p-12 animate-fade-in">
              <i className="ri-shopping-cart-line text-6xl text-gray-500 mb-6 animate-bounce"></i>
              <h1 className="text-3xl font-bold text-white mb-4">Votre panier est vide</h1>
              <p className="text-gray-400 mb-8">Découvrez notre collection et ajoutez des produits à votre panier</p>
              <Link
                to="/boutique"
                className="inline-block bg-gradient-to-r from-[#FF8A00] to-[#FF6B00] text-[#0A0A0A] font-bold px-8 py-3 rounded-lg hover:from-[#FF6B00] hover:to-[#FF8A00] transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-[#FF8A00]/50"
              >
                <i className="ri-store-line mr-2"></i>
                Découvrir la boutique
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Header />
      
      <main className="pt-20 pb-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-white">Mon Panier</h1>
            <div className="text-right">
              <p className="text-gray-400 text-sm">{cart.length} article{cart.length > 1 ? 's' : ''}</p>
              <p className="text-2xl font-bold text-[#FF8A00]">{total.toFixed(2)}€</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="bg-gray-900 rounded-lg p-6 mb-6">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                  <i className="ri-shopping-bag-line mr-2 text-[#FF8A00]"></i>
                  Articles ({cart.length})
                </h2>
                
                <div className="space-y-6">
                  {cart.map((item) => (
                    <div key={item.cartId} className="flex gap-4 p-4 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors">
                      <div className="relative">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-20 h-24 object-cover rounded"
                          loading="lazy"
                        />
                        <div className="absolute -top-2 -right-2">
                          <button
                            onClick={() => removeFromCart(item.cartId)}
                            className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                            title="Supprimer"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="text-white font-semibold mb-1">{item.name}</h3>
                        <p className="text-gray-400 text-sm mb-2">Taille: {item.selectedSize}</p>
                        <p className="text-[#FF8A00] font-bold text-lg">{item.price}€</p>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.cartId, item.quantity - 1)}
                            className="w-8 h-8 bg-gray-700 text-white rounded flex items-center justify-center hover:bg-gray-600 transition-colors"
                            disabled={item.quantity <= 1}
                          >
                            -
                          </button>
                          <span className="text-white w-8 text-center font-semibold">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
                            className="w-8 h-8 bg-gray-700 text-white rounded flex items-center justify-center hover:bg-gray-600 transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <p className="text-gray-400 text-sm">
                          Total: {(item.price * item.quantity).toFixed(2)}€
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping Information */}
              <div className="bg-gray-900 rounded-lg p-6 mb-6">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                  <i className="ri-truck-line mr-2 text-[#FF8A00]"></i>
                  Informations de livraison
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Prénom *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={shippingInfo.firstName}
                      onChange={(e) => setShippingInfo({...shippingInfo, firstName: e.target.value})}
                      className={`w-full bg-gray-800 text-white border rounded-lg px-4 py-3 focus:border-[#FF8A00] focus:outline-none transition-colors ${
                        errors.firstName ? 'border-red-500' : 'border-gray-700'
                      }`}
                      required
                    />
                    {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Nom *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={shippingInfo.lastName}
                      onChange={(e) => setShippingInfo({...shippingInfo, lastName: e.target.value})}
                      className={`w-full bg-gray-800 text-white border rounded-lg px-4 py-3 focus:border-[#FF8A00] focus:outline-none transition-colors ${
                        errors.lastName ? 'border-red-500' : 'border-gray-700'
                      }`}
                      required
                    />
                    {errors.lastName && <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={shippingInfo.email}
                      onChange={(e) => setShippingInfo({...shippingInfo, email: e.target.value})}
                      className={`w-full bg-gray-800 text-white border rounded-lg px-4 py-3 focus:border-[#FF8A00] focus:outline-none transition-colors ${
                        errors.email ? 'border-red-500' : 'border-gray-700'
                      }`}
                      required
                    />
                    {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Téléphone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={shippingInfo.phone}
                      onChange={(e) => setShippingInfo({...shippingInfo, phone: e.target.value})}
                      className={`w-full bg-gray-800 text-white border rounded-lg px-4 py-3 focus:border-[#FF8A00] focus:outline-none transition-colors ${
                        errors.phone ? 'border-red-500' : 'border-gray-700'
                      }`}
                      placeholder="06 12 34 56 78"
                      required
                    />
                    {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-white text-sm font-semibold mb-2">Adresse *</label>
                    <input
                      type="text"
                      name="address"
                      value={shippingInfo.address}
                      onChange={(e) => setShippingInfo({...shippingInfo, address: e.target.value})}
                      className={`w-full bg-gray-800 text-white border rounded-lg px-4 py-3 focus:border-[#FF8A00] focus:outline-none transition-colors ${
                        errors.address ? 'border-red-500' : 'border-gray-700'
                      }`}
                      required
                    />
                    {errors.address && <p className="text-red-400 text-xs mt-1">{errors.address}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Ville *</label>
                    <input
                      type="text"
                      name="city"
                      value={shippingInfo.city}
                      onChange={(e) => setShippingInfo({...shippingInfo, city: e.target.value})}
                      className={`w-full bg-gray-800 text-white border rounded-lg px-4 py-3 focus:border-[#FF8A00] focus:outline-none transition-colors ${
                        errors.city ? 'border-red-500' : 'border-gray-700'
                      }`}
                      required
                    />
                    {errors.city && <p className="text-red-400 text-xs mt-1">{errors.city}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Code postal *</label>
                    <input
                      type="text"
                      name="postalCode"
                      value={shippingInfo.postalCode}
                      onChange={(e) => setShippingInfo({...shippingInfo, postalCode: e.target.value})}
                      className={`w-full bg-gray-800 text-white border rounded-lg px-4 py-3 focus:border-[#FF8A00] focus:outline-none transition-colors ${
                        errors.postalCode ? 'border-red-500' : 'border-gray-700'
                      }`}
                      placeholder="75001"
                      required
                    />
                    {errors.postalCode && <p className="text-red-400 text-xs mt-1">{errors.postalCode}</p>}
                  </div>
                </div>
              </div>

              {/* Billing Information */}
              <div className="bg-gray-900 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                  <i className="ri-bill-line mr-2 text-[#FF8A00]"></i>
                  Informations de facturation
                </h2>
                
                <div className="mb-4">
                  <label className="flex items-center gap-3 text-white cursor-pointer">
                    <input
                      type="checkbox"
                      checked={billingInfo.sameAsShipping}
                      onChange={(e) => setBillingInfo({...billingInfo, sameAsShipping: e.target.checked})}
                      className="w-4 h-4 text-[#FF8A00] bg-gray-800 border-gray-600 rounded focus:ring-[#FF8A00] focus:ring-2"
                    />
                    <span>Identique à l'adresse de livraison</span>
                  </label>
                </div>

                {!billingInfo.sameAsShipping && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">Prénom *</label>
                      <input
                        type="text"
                        name="billing_firstName"
                        value={billingInfo.firstName}
                        onChange={(e) => setBillingInfo({...billingInfo, firstName: e.target.value})}
                        className={`w-full bg-gray-800 text-white border rounded-lg px-4 py-3 focus:border-[#FF8A00] focus:outline-none transition-colors ${
                          errors.billing_firstName ? 'border-red-500' : 'border-gray-700'
                        }`}
                        required
                      />
                      {errors.billing_firstName && <p className="text-red-400 text-xs mt-1">{errors.billing_firstName}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">Nom *</label>
                      <input
                        type="text"
                        name="billing_lastName"
                        value={billingInfo.lastName}
                        onChange={(e) => setBillingInfo({...billingInfo, lastName: e.target.value})}
                        className={`w-full bg-gray-800 text-white border rounded-lg px-4 py-3 focus:border-[#FF8A00] focus:outline-none transition-colors ${
                          errors.billing_lastName ? 'border-red-500' : 'border-gray-700'
                        }`}
                        required
                      />
                      {errors.billing_lastName && <p className="text-red-400 text-xs mt-1">{errors.billing_lastName}</p>}
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-white text-sm font-semibold mb-2">Adresse *</label>
                      <input
                        type="text"
                        name="billing_address"
                        value={billingInfo.address}
                        onChange={(e) => setBillingInfo({...billingInfo, address: e.target.value})}
                        className={`w-full bg-gray-800 text-white border rounded-lg px-4 py-3 focus:border-[#FF8A00] focus:outline-none transition-colors ${
                          errors.billing_address ? 'border-red-500' : 'border-gray-700'
                        }`}
                        required
                      />
                      {errors.billing_address && <p className="text-red-400 text-xs mt-1">{errors.billing_address}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">Ville *</label>
                      <input
                        type="text"
                        name="billing_city"
                        value={billingInfo.city}
                        onChange={(e) => setBillingInfo({...billingInfo, city: e.target.value})}
                        className={`w-full bg-gray-800 text-white border rounded-lg px-4 py-3 focus:border-[#FF8A00] focus:outline-none transition-colors ${
                          errors.billing_city ? 'border-red-500' : 'border-gray-700'
                        }`}
                        required
                      />
                      {errors.billing_city && <p className="text-red-400 text-xs mt-1">{errors.billing_city}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-white text-sm font-semibold mb-2">Code postal *</label>
                      <input
                        type="text"
                        name="billing_postalCode"
                        value={billingInfo.postalCode}
                        onChange={(e) => setBillingInfo({...billingInfo, postalCode: e.target.value})}
                        className={`w-full bg-gray-800 text-white border rounded-lg px-4 py-3 focus:border-[#FF8A00] focus:outline-none transition-colors ${
                          errors.billing_postalCode ? 'border-red-500' : 'border-gray-700'
                        }`}
                        required
                      />
                      {errors.billing_postalCode && <p className="text-red-400 text-xs mt-1">{errors.billing_postalCode}</p>}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-gray-900 rounded-lg p-6 sticky top-24">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                  <i className="ri-file-list-line mr-2 text-[#FF8A00]"></i>
                  Récapitulatif
                </h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-white">
                    <span>Sous-total</span>
                    <span>{subtotal.toFixed(2)}€</span>
                  </div>
                  
                  <div className="flex justify-between text-white">
                    <span>Livraison</span>
                    <span className={shippingCost === 0 ? 'text-green-400' : ''}>
                      {shippingCost === 0 ? 'Gratuite' : `${shippingCost.toFixed(2)}€`}
                    </span>
                  </div>
                  
                  {subtotal < 90 && (
                    <div className="text-sm text-gray-400 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                      <i className="ri-information-line mr-2"></i>
                      Livraison gratuite à partir de 90€
                      <br />
                      <span className="text-blue-400">Plus que {(90 - subtotal).toFixed(2)}€ !</span>
                    </div>
                  )}
                  
                  <div className="border-t border-gray-700 pt-4">
                    <div className="flex justify-between text-xl font-bold text-white">
                      <span>Total</span>
                      <span className="text-[#FF8A00]">{total.toFixed(2)}€</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={isLoading || cart.length === 0}
                  className="w-full bg-gradient-to-r from-[#FF8A00] to-[#FF6B00] text-[#0A0A0A] font-bold py-4 rounded-lg hover:from-[#FF6B00] hover:to-[#FF8A00] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transform hover:scale-105 shadow-lg hover:shadow-[#FF8A00]/50"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div>
                      Redirection...
                    </>
                  ) : (
                    <>
                      <i className="ri-secure-payment-line"></i>
                      Procéder au paiement
                    </>
                  )}
                </button>

                <div className="mt-4 text-center">
                  <Link
                    to="/boutique"
                    className="text-[#FF8A00] hover:text-[#FF6B00] transition-colors text-sm flex items-center justify-center gap-1"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Continuer mes achats
                  </Link>
                </div>

                {/* Trust Badges */}
                <div className="mt-6 grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 p-3 bg-gray-800/50 rounded-lg">
                    <i className="ri-truck-line text-[#FF8A00] text-lg"></i>
                    <div>
                      <p className="text-white font-semibold text-xs">Livraison 5j</p>
                      <p className="text-gray-400 text-xs">Gratuite dès 90€</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-gray-800/50 rounded-lg">
                    <i className="ri-shield-check-line text-[#FF8A00] text-lg"></i>
                    <div>
                      <p className="text-white font-semibold text-xs">Retours 30j</p>
                      <p className="text-gray-400 text-xs">Gratuits</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-gray-800/50 rounded-lg">
                    <i className="ri-secure-payment-line text-[#FF8A00] text-lg"></i>
                    <div>
                      <p className="text-white font-semibold text-xs">Paiement</p>
                      <p className="text-gray-400 text-xs">100% sécurisé</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-gray-800/50 rounded-lg">
                    <i className="ri-customer-service-line text-[#FF8A00] text-lg"></i>
                    <div>
                      <p className="text-white font-semibold text-xs">Support</p>
                      <p className="text-gray-400 text-xs">24/7</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CartPage;
